import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { collection, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { Organization, UserProfile } from '@types';
import { db, functions } from '../../firebaseConfig';
import { ConsultantReportViewer } from './ConsultantReportViewer';
import { ReportNavigationContext } from '../common/reportNavigation';

type Notification = { id: string; type?: string; title: string; message: string; requestReference?: string; ticketId?: string; reportId?: string };

const ClientServiceRequestNotifications: React.FC<{ currentUser: UserProfile; organization: Organization; onOpen: (page: 'help' | 'serviceRequests') => void }> = ({ currentUser, organization, onOpen }) => {
    const [notification, setNotification] = useState<Notification | null>(null);
    const [reportId, setReportId] = useState('');
    const navigateReport = React.useContext(ReportNavigationContext);
    const openReport = navigateReport || setReportId;

    useEffect(() => {
        const url = new URL(window.location.href);
        const target = url.searchParams.get('consultantReport');
        if (target && /^consultant-[\w-]{1,128}$/.test(target)) {
            openReport(target);
            url.searchParams.delete('consultantReport');
            window.history.replaceState(window.history.state, '', url);
        }
    }, []);

    useEffect(() => onSnapshot(query(
        collection(db, 'client_notifications'),
        where('recipientUid', '==', currentUser.uid),
        where('organizationId', '==', organization.id),
        where('read', '==', false),
        orderBy('createdAt', 'desc'),
        limit(1),
    ), snapshot => {
        const document = snapshot.docs[0];
        setNotification(document ? { id: document.id, ...document.data() } as Notification : null);
    }, error => console.error('Client service notification failed loading.', error)), [currentUser.uid, organization.id]);

    useEffect(() => {
        const update = () => void httpsCallable(functions, 'manageServiceRequests')({ action: 'updateClientNotificationPresence', organizationId: organization.id,
            visible: document.visibilityState === 'visible' }).catch(error => console.error('Client notification presence failed updating.', error));
        update();
        const timer = window.setInterval(update, 60000);
        document.addEventListener('visibilitychange', update);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', update);
            void httpsCallable(functions, 'manageServiceRequests')({ action: 'updateClientNotificationPresence', organizationId: organization.id,
                visible: false }).catch(error => console.error('Client notification presence failed closing.', error));
        };
    }, [organization.id, currentUser.uid]);

    if (!notification && !reportId)
        return null;
    const isTicket = notification?.type === 'helpdesk_ticket_resolved';

    const dismiss = async (open: boolean) => {
        const selected = notification;
        if (!selected)
            return;
        setNotification(null);
        if (open && selected.type === 'consultant_report' && selected.reportId)
            openReport(selected.reportId);
        else if (open)
            onOpen(isTicket ? 'help' : 'serviceRequests');
        try {
            await httpsCallable(functions, 'manageServiceRequests')({ action: 'markClientNotificationRead', notificationId: selected.id });
        } catch (error)
            { console.error('Client service notification failed dismissing.', error); }
    };

    return <>{notification && createPortal(<button type="button" onClick={() => void dismiss(true)} className="fixed bottom-6 left-6 z-[1000] w-[min(24rem,calc(100vw-3rem))] rounded-2xl border border-emerald-400/40 bg-slate-900 p-5 text-left text-white shadow-2xl hover:bg-slate-800">
        <div className="flex items-start justify-between gap-4"><div><p className="text-[10px] font-black uppercase tracking-widest text-emerald-300">{notification.type === 'consultant_report' ? 'New Report' : isTicket ? 'Support Ticket Update' : 'Service Request Update'}</p><h3 className="mt-1 font-extrabold">{notification.title}</h3><p className="mt-2 text-sm text-slate-300">{notification.message}</p><p className="mt-3 text-xs font-bold text-emerald-300">{notification.type === 'consultant_report' ? 'Open report →' : isTicket ? `Ticket #${notification.ticketId?.slice(-6) || ''} · View ticket →` : `${notification.requestReference || ''} · View request →`}</p></div><span onClick={event => { event.stopPropagation(); void dismiss(false); }} className="rounded-lg px-2 py-1 text-lg text-slate-400 hover:bg-white/10 hover:text-white">×</span></div>
    </button>, document.body)}{reportId && <ConsultantReportViewer reportId={reportId} onClose={() => setReportId('')} />}</>;
};

export default ClientServiceRequestNotifications;
